import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Star, Save, Zap, Gift } from 'lucide-react';

const CAL_COLS = [
  { key: 'active', label: 'Active', cls: 'text-emerald-700', dot: 'bg-emerald-500' },
  { key: 'expiring_this_week', label: 'Expiring this week', cls: 'text-amber-700', dot: 'bg-amber-500' },
  { key: 'scheduled', label: 'Scheduled', cls: 'text-sky-700', dot: 'bg-sky-500' },
];

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'; } catch { return '—'; } };

const AdminFeaturedJobs = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jobId, setJobId] = useState('');
  const [busy, setBusy] = useState(false);
  const [cal, setCal] = useState(null);
  const [trials, setTrials] = useState(null);

  useEffect(() => {
    document.title = 'Featured Job Settings | Admin';
    axios.get(`${API_URL}/admin/featured-job/config`).then((r) => setCfg(r.data)).catch(() => toast.error('Failed to load settings'));
    axios.get(`${API_URL}/admin/featured-jobs/calendar`).then((r) => setCal(r.data)).catch(() => setCal(null));
    axios.get(`${API_URL}/admin/internship-trials/stats`).then((r) => setTrials(r.data)).catch(() => setTrials(null));
  }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API_URL}/admin/featured-job/config`, cfg); setCfg(r.data.config); toast.success('Settings saved'); }
    catch (e) { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const featureJob = async (featured) => {
    if (!jobId.trim()) { toast.error('Enter a job ID'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/admin/jobs/${jobId.trim()}/feature-direct`, { featured, urgently_hiring: featured });
      toast.success(r.data.message);
      axios.get(`${API_URL}/admin/featured-jobs/calendar`).then((res) => setCal(res.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); } finally { setBusy(false); }
  };

  const calAction = async (jid, action) => {
    let extra = {};
    if (action === 'extend') {
      const days = parseInt(window.prompt('Extend featured placement by how many days?', '7'), 10);
      if (!days || Number.isNaN(days)) return;
      extra = { days };
    }
    if (action === 'cancel' && !window.confirm('Cancel this featured placement now? Priority and Urgently Hiring will be removed.')) return;
    try {
      const r = await axios.post(`${API_URL}/admin/jobs/${jid}/featured-action`, { action, ...extra });
      toast.success(r.data.message);
      const res = await axios.get(`${API_URL}/admin/featured-jobs/calendar`);
      setCal(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  if (!cfg) return <div className="pt-24 text-center text-gray-500">Loading…</div>;

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-24 pb-16" data-testid="admin-featured-jobs">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <h1 className="font-serif text-3xl font-bold text-primary flex items-center gap-2"><Star className="w-6 h-6 text-secondary" /> Featured Job Settings</h1>

        {/* Free 30-Day Internship Trial analytics */}
        {trials && (
          <div className="bg-white rounded-2xl border p-6" data-testid="trial-analytics">
            <h2 className="font-serif text-lg font-bold text-primary mb-4 flex items-center gap-2"><Gift className="w-5 h-5 text-secondary" /> Free 30-Day Internship Trials</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { k: 'claimed', label: 'Claimed', cls: 'text-primary' },
                { k: 'active', label: 'Active', cls: 'text-emerald-700' },
                { k: 'expired', label: 'Expired', cls: 'text-gray-600' },
                { k: 'converted', label: 'Converted to paid', cls: 'text-secondary' },
              ].map((s) => (
                <div key={s.k} className="rounded-xl border bg-gray-50 p-3 text-center" data-testid={`trial-stat-${s.k}`}>
                  <p className={`text-2xl font-bold ${s.cls}`}>{trials[s.k]}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                </div>
              ))}
              <div className="rounded-xl border bg-gray-50 p-3 text-center" data-testid="trial-stat-rate">
                <p className="text-2xl font-bold text-amber-700">{trials.conversion_rate}%</p>
                <p className="text-xs text-gray-500 mt-0.5">Conversion rate</p>
              </div>
            </div>
            {trials.recent?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Recent claims</p>
                <div className="divide-y border rounded-xl">
                  {trials.recent.map((c, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate">{c.org_name || '—'} <span className="text-gray-400">· {c.email}</span></span>
                      <span className="text-xs text-gray-400">{fmt(c.claimed_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border p-6 space-y-4">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setField('enabled', e.target.checked)} className="w-4 h-4" data-testid="fj-enabled" />
            <span className="font-semibold text-gray-700">Enable payment collection (turn OFF = "Coming Soon")</span>
          </label>
          <p className="text-xs text-gray-500 -mt-2">Leave OFF until Stripe checkout is wired &amp; tested.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Price ($)</Label><Input type="number" value={cfg.price} onChange={(e) => setField('price', Number(e.target.value))} data-testid="fj-price" /></div>
            <div><Label>Featured duration (days)</Label><Input type="number" value={cfg.duration_days} onChange={(e) => setField('duration_days', Number(e.target.value))} data-testid="fj-duration" /></div>
            <div><Label>Candidate invitations per job</Label><Input type="number" value={cfg.invite_allowance} onChange={(e) => setField('invite_allowance', Number(e.target.value))} data-testid="fj-invites" /></div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={cfg.urgently_hiring_enabled} onChange={(e) => setField('urgently_hiring_enabled', e.target.checked)} className="w-4 h-4" data-testid="fj-urgent-enabled" />
                <span className="text-sm text-gray-700">Allow "Urgently Hiring" label</span>
              </label>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-primary text-white" data-testid="fj-save"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Settings'}</Button>
        </div>

        <div className="bg-white rounded-2xl border p-6 space-y-3">
          <h2 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><Zap className="w-5 h-5 text-red-600" /> Manually Feature a Direct Job</h2>
          <p className="text-sm text-gray-500">Enter a job ID (verified-employer, non-imported jobs only). Feature applies priority placement, the Featured badge, and enables candidate invitations for the configured duration.</p>
          <div className="flex gap-2">
            <Input value={jobId} onChange={(e) => setJobId(e.target.value)} placeholder="job_id" data-testid="fj-jobid" />
            <Button onClick={() => featureJob(true)} disabled={busy} className="bg-secondary text-white" data-testid="fj-feature">Feature</Button>
            <Button onClick={() => featureJob(false)} disabled={busy} variant="outline" data-testid="fj-unfeature">Unfeature</Button>
          </div>
        </div>

        {/* Featured Placement Calendar */}
        <div className="bg-white rounded-2xl border p-6" data-testid="featured-calendar">
          <h2 className="font-serif text-lg font-bold text-primary mb-1">Featured Placement Calendar</h2>
          <p className="text-sm text-gray-500 mb-4">Which featured jobs are active, expiring this week, or scheduled — at a glance.</p>
          {!cal ? <p className="text-gray-400 text-sm">Loading…</p> : (
            <div className="grid md:grid-cols-3 gap-4">
              {CAL_COLS.map((col) => (
                <div key={col.key} className="rounded-xl border bg-gray-50/60 p-3" data-testid={`cal-col-${col.key}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                    <h3 className={`text-sm font-bold ${col.cls}`}>{col.label}</h3>
                    <span className="ml-auto text-xs font-semibold text-gray-400">{(cal[col.key] || []).length}</span>
                  </div>
                  {(cal[col.key] || []).length === 0 ? (
                    <p className="text-xs text-gray-400 py-2">None</p>
                  ) : (
                    <div className="space-y-2">
                      {(cal[col.key] || []).map((j) => (
                        <div key={j.job_id} className="bg-white rounded-lg border p-2.5" data-testid={`cal-item-${j.job_id}`}>
                          <a href={`/jobs/${j.job_id}`} className="text-sm font-semibold text-primary hover:underline line-clamp-1">{j.title}</a>
                          <p className="text-[11px] text-gray-500 line-clamp-1">{j.company_name}{j.location ? ` · ${j.location}` : ''}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[11px] text-gray-500">{col.key === 'scheduled' ? `Starts ${fmt(j.start)}` : `Ends ${fmt(j.until)}`}</span>
                            {j.urgently_hiring && <span className="text-[10px] bg-red-600 text-white font-bold px-1.5 py-0.5 rounded-full">Urgent</span>}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            <button onClick={() => calAction(j.job_id, 'pause')} className="text-[10px] font-semibold px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`cal-pause-${j.job_id}`}>Pause</button>
                            <button onClick={() => calAction(j.job_id, 'extend')} className="text-[10px] font-semibold px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`cal-extend-${j.job_id}`}>Extend</button>
                            <button onClick={() => calAction(j.job_id, 'cancel')} className="text-[10px] font-semibold px-2 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50" data-testid={`cal-cancel-${j.job_id}`}>Cancel</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFeaturedJobs;
